import React from 'react';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown-now';
import withBangingAnimator from 'containers/withBangingAnimator';
import withPendingSpinEnd from 'containers/withPendingSpinEnd';
import Ranking, { DISPLAY_USER } from 'components/Ranking';
import TruncatingTextBlock from 'components/TruncatingTextBlock';
import { padNumber } from 'utils/formatting';
import { userShape } from 'utils/shapes';
import shouldUpdate from 'utils/shouldUpdate';
import { TOURNAMENT_STATUS_UPCOMING } from 'utils/tournaments';
import './TournamentLeaderboard.css';

const MAX_DESCRIPTION = 100;
const AnimatedRanking = withPendingSpinEnd({
  name: 'score',
  spinEndBufferMs: 1000
})(
  withBangingAnimator({
    name: 'score'
  })(Ranking)
);

const renderer = ({ label, days, hours, minutes, seconds }) => {
  let output = '';
  if (days) {
    output += padNumber(days) + ':';
  }
  if (days || hours) {
    output += padNumber(hours) + ':';
  }
  output += padNumber(minutes) + ':' + padNumber(seconds);
  return (
    <div>
      <div>{label + ':'}</div>
      <div className="notranslate">{output}</div>
    </div>
  );
};

function TournamentLeaderboard({
  tournament,
  userScore,
  totalPlayers,
  user,
  ranks,
  countdownTo
}) {
  const lRanks = ranks.slice(0);

  lRanks.sort((a, b) => {
    return a.rank === b.rank ? 0 : a.rank < b.rank ? -1 : 1;
  });

  const description = tournament && tournament.description;
  const userInGroup = tournament && tournament.userInGroup;
  const places =
    tournament &&
    tournament.winningPlaces &&
    tournament.winningPlaces.winningPlaces &&
    tournament.winningPlaces.winningPlaces.map(p => p.place);
  const upcoming =
    tournament && tournament.status === TOURNAMENT_STATUS_UPCOMING;

  const rankings = upcoming ? (
    <div className="notice">Tournament will begin shortly</div>
  ) : (
    <div className="rankings">
      {lRanks.map((element, i) => (
        <Ranking key={i} places={places} {...element} />
      ))}
    </div>
  );

  const countdownLabel = upcoming ? 'Starting In' : 'Time Remaining';

  return (
    <div className="tournament-leaderboard">
      <div className="header">
        <TruncatingTextBlock
          className="prize"
          text={description}
          tooltipClass="leaderboard-prize-tooltip"
          maxLength={MAX_DESCRIPTION}
          maxHeight="4.5em"
        />
        <div className="stats">
          <div className="players">{`${totalPlayers || '-'} players`}</div>
          <div className="timer">
            <Countdown
              key={countdownLabel}
              date={countdownTo}
              renderer={renderer}
              label={countdownLabel}
            />
          </div>
        </div>
      </div>
      {rankings}
      {userInGroup && (
        <div className="user-rank">
          <AnimatedRanking
            display={DISPLAY_USER}
            rank={userScore.rank}
            player={user}
            score={userScore.score}
            places={places}
          />
        </div>
      )}
    </div>
  );
}

TournamentLeaderboard.propTypes = {
  tournament: PropTypes.object,
  userScore: PropTypes.shape({
    score: PropTypes.number,
    rank: PropTypes.number
  }),
  totalPlayers: PropTypes.number,
  countdownTo: PropTypes.number,
  user: userShape,
  ranks: PropTypes.arrayOf(
    PropTypes.shape({
      player: userShape,
      rank: PropTypes.number,
      score: PropTypes.number
    })
  )
};

function shouldComponentUpdate(props, nextProps) {
  return (
    props.tournament !== nextProps.tournament ||
    props.userScore !== nextProps.userScore ||
    props.totalPlayers !== nextProps.totalPlayers ||
    props.countdownTo !== nextProps.countdownTo ||
    props.user !== nextProps.user ||
    props.ranks !== nextProps.ranks
  );
}

export default shouldUpdate(shouldComponentUpdate)(TournamentLeaderboard);
